import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { QueryKey, useMutation, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';

import { addSnackbar, SnackbarPayload } from 'theSource/components';
import { api } from './api';
import { setAuthenticated } from 'theSource/features';
import { isSessionExpired } from 'theSource/utils';
import { ForbiddenResponseDto } from 'theSource/types';

interface UseApiMutationProps<TResponse, TRequest, TError> {
  queryKey: QueryKey;
  queryKeysToInvalidate?: QueryKey;
  clearQueryCache?: Boolean;
  invalidateQueriesCallback?: () => void;
  method: 'post' | 'put' | 'delete';
  endpoint: string;
  onSuccess?: (data: TResponse, requestVariables: TRequest) => void;
  onError?: (error: AxiosError<TError>, requestVariables: TRequest) => void;
  getSuccessMessage?: (error: TResponse) => string | undefined;
  getErrorMessage?: (error: AxiosError<TError>) => string | undefined;
  showSuccessSnackbar?: boolean;
  showErrorSnackbar?: boolean;
  snackbarOptions?: Partial<SnackbarPayload>;
  toastRef?: {
    showToast: (options: {
      message: string;
      status: 'info' | 'warning' | 'success' | 'danger';
      closeIcon?: boolean;
      targetClass?: string;
    }) => void;
  };
}

export const useApiMutation = <TResponse, TError, TRequest>({
  queryKey,
  queryKeysToInvalidate,
  clearQueryCache = false,
  invalidateQueriesCallback,
  method,
  endpoint,
  onSuccess,
  onError,
  getSuccessMessage,
  getErrorMessage,
  showSuccessSnackbar = true,
  showErrorSnackbar = true,
  snackbarOptions,
  toastRef,
}: UseApiMutationProps<TResponse, TRequest, TError>) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  return useMutation<TResponse, AxiosError<TError>, TRequest>(
    queryKey,
    (data) => api[method]<TRequest, TResponse>(endpoint, data),
    {
      onSuccess: (data, requestVariables) => {
        onSuccess?.(data, requestVariables);

        if (queryKeysToInvalidate) {
          queryClient
            .invalidateQueries(queryKeysToInvalidate)
            .then(() => invalidateQueriesCallback?.());
        }

        if (clearQueryCache) {
          queryClient.clear();
        }

        if (showSuccessSnackbar) {
          const successMessage =
            String(getSuccessMessage?.(data) ?? t('saveSuccessful')!);
          if (toastRef) {
            toastRef.showToast({
              message: successMessage,
              status: 'success',
              closeIcon: true
            });
          } else {
            dispatch(
              addSnackbar({
                message: successMessage,
                severity: 'success',
                ...snackbarOptions,
              })
            );
          }
        }
      },
      onError: (error, requestVariables) => {
        if ((error as AxiosError<ForbiddenResponseDto>) && isSessionExpired(error as AxiosError<ForbiddenResponseDto>)) {
          dispatch(setAuthenticated(false));
          return;
        }

        onError?.(error, requestVariables);

        if (showErrorSnackbar) {
          const errorMessage = String(getErrorMessage?.(error) ?? t('serverError')!);
          if (toastRef) {
            toastRef.showToast({
              message: errorMessage,
              status: 'danger',
              closeIcon: true
            });
          } else {
            dispatch(
              addSnackbar({
                message: errorMessage,
                severity: 'error',
                ...snackbarOptions,
              })
            );
          }
        }
      },
    }
  );
};
